/**
 * Testo "Guadagna N punti" in PDP al posto del widget Yotpo Product Points,
 * che su questo tema non si mostra (vedi snippets/yotpo-points-fallback.liquid).
 *
 * - Punti: stessa chiamata del widget, POST product_potential_points.
 * - Testi: logged-in-text / logged-out-text configurati nell'admin Yotpo
 *   (letti da window.yotpoWidgetsContainer), con fallback sulle traduzioni.
 * - Cambio variante: evento on:variant:change del variant-picker.
 * - Se il widget originale compare (#yotpo-product-points-widget), questo
 *   testo si svuota per non mostrare i punti due volte.
 */
if (!customElements.get('yotpo-points-fallback')) {
  class YotpoPointsFallback extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        return;
      }
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.variantId = Number(this.dataset.variantId);
      this.points = null;
      this.textEl = document.createElement('p');
      this.textEl.className = 'yotpo-points-fallback__text';
      this.appendChild(this.textEl);

      const picker = this.closest('.product')?.querySelector('variant-picker');
      if (picker) {
        picker.addEventListener('on:variant:change', (evt) => {
          if (evt.detail && evt.detail.variant) {
            this.variantId = Number(evt.detail.variant.id);
            this.render();
          }
        });
      }

      this.waitForYotpoConfig().then((config) => this.load(config));
    }

    /**
     * Aspetta fino a 10s la configurazione del widget caricata da Yotpo.
     * Senza (loader bloccato, per esempio dal consenso cookie) si prosegue
     * con lo store id del tema e i testi di fallback.
     * @returns {Promise<object|null>}
     */
    waitForYotpoConfig() {
      const read = () => {
        const guids = window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.guids;
        if (!guids) return null;
        const guid = Object.values(guids).find((g) => g.config?.widgets?.[this.dataset.instanceId]);
        return guid ? guid.config.widgets[this.dataset.instanceId] : null;
      };
      return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
          const config = read();
          if (config || Date.now() - start > 10000) resolve(config);
          else setTimeout(tick, 250);
        };
        tick();
      });
    }

    async load(config) {
      const custom = (config && config.customizations) || {};
      if (config && config.staticContent && config.staticContent.isHidden) return;

      const storeId = (config && config.staticContent && config.staticContent.storeId) || this.dataset.storeId;
      this.template = (this.loggedIn ? custom['logged-in-text'] : custom['logged-out-text'])
        || this.decodeEntities(this.loggedIn ? this.data.text_logged_in : this.data.text_logged_out);
      const applyTagsRestrictions = custom['apply-tags-restrictions'] !== false;

      try {
        const response = await fetch(`https://storefront.yotpo.com/loyalty/v3/stores/${storeId}/product_potential_points`, {
          method: 'POST',
          headers: { accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_potential_points: {
              variants: this.data.variants,
              product_id: this.data.product_id,
              collection_ids: this.data.collection_ids,
              product_tags: this.data.product_tags,
              product_type: this.data.product_type,
              ignore_tags_restrictions: !applyTagsRestrictions
            }
          })
        });
        if (!response.ok) return;
        const json = await response.json();
        this.points = json?.product_potential_points?.variants_with_points || [];
      } catch (e) {
        return;
      }
      this.render();
      this.watchNativeWidget();
    }

    render() {
      if (!this.points || !this.template || this.nativeVisible()) {
        this.hide();
        return;
      }
      const match = this.points.find((v) => Number(v.id) === this.variantId);
      const amount = match ? match.points : 0;
      if (!amount) {
        this.hide();
        return;
      }
      this.textEl.innerHTML = this.format(this.template.replace(/{{\s*points_amount\s*}}/g, amount));
      this.hidden = false;
    }

    hide() {
      this.textEl.textContent = '';
      this.hidden = true;
    }

    nativeVisible() {
      const native = document.getElementById('yotpo-product-points-widget');
      return !!(native && native.offsetHeight > 0);
    }

    /**
     * Se il widget Yotpo originale si mostra piu' tardi, lascia il posto a lui.
     */
    watchNativeWidget() {
      const container = this.parentElement;
      if (!container) return;
      new MutationObserver(() => {
        if (this.nativeVisible()) this.hide();
      }).observe(container, { childList: true, subtree: true });
    }

    /**
     * I link dei testi Yotpo puntano al dominio tecnico dello store
     * (intersport-it-prod.myshopify.com/account/login): si riportano sul
     * dominio che si sta navigando, strumentoarteshop.com, tenendo solo il percorso.
     * @param {string} url
     * @returns {string|null} href sicuro, o null se il link non e' http(s)/relativo
     */
    localizeUrl(url) {
      if (!/^(https?:\/\/|\/)/.test(url)) return null;
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.hostname.endsWith('.myshopify.com')) {
          return parsed.pathname + parsed.search + parsed.hash;
        }
        return parsed.href;
      } catch (e) {
        return null;
      }
    }

    /**
     * Il filtro `t` con variabili escapa il testo (l'apostrofo arriva come
     * &#39;): va riportato in chiaro prima di format(), che escapa di nuovo.
     * @param {string} text
     * @returns {string}
     */
    decodeEntities(text) {
      if (!text) return text;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }

    /**
     * Markdown minimo dei testi Yotpo: **grassetto**, [testo](url), a capo.
     * Il testo viene prima escapato: arriva dalla configurazione Yotpo.
     * @param {string} text
     * @returns {string}
     */
    format(text) {
      const escape = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      return escape(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, url) => {
          const href = this.localizeUrl(url.replace(/&amp;/g, '&'));
          return href ? `<a href="${escape(href)}">${label}</a>` : label;
        })
        .replace(/\n/g, '<br>');
    }
  }

  customElements.define('yotpo-points-fallback', YotpoPointsFallback);
}
